<template>
    <div class="JsonSchemaAddressForm">
        <label v-if="form.schema.title" class="JsonSchemaAddressForm__title">
            {{ form.schema.title }}
        </label>
        <div v-if="required || valueInitialized" class="JsonSchemaAddressForm__content">
            <!-- TODO may need to break down to fixed properties when adding auto fill feature -->
            <component
                :is="formElementComponent"
                v-for="property in properties"
                :id="`${id}_${property.name}`"
                :key="property.form.key"
                :depth="depth + 1"
                :form="property.form"
                :required="(required || valueInitialized) && property.required"
                :disabled="disabled || property.form.schema.readOnly"
                :form-element-component="formElementComponent"
                :custom-component-input="customComponentInput"
                :value="getPropValue(property)"
                :touched="childTouchState[property.name] || touched"
                :no-local-touch="!touched"
                :strings="strings"
                :wrap-value-mode="wrapValueMode"
                v-bind="$attrs"
                @input="onInput(property, $event)"
                @change="onChange(property, $event)"
                @touch="updateTouchState(property.name)"
            >
                <template #icon>
                    <slot name="icon" />
                </template>
            </component>
            <div v-if="deletable" class="JsonSchemaAddressForm__deleteIconContainer">
                <NewButton
                    variant="flat"
                    size="icon-sm"
                    color="secondary"
                    class="JsonSchemaAddressForm__deleteIcon JsonSchemaForm__deleteIcon"
                    @click="onDelete"
                >
                    <FontAwesomeIcon :icon="faXmark" />
                </NewButton>
            </div>
        </div>
        <FormInput force-action-slot>
            <Button
                v-if="!required && !valueInitialized"
                class="JsonSchemaAddressForm__addButton"
                type="primary"
                :disabled="valueInitialized"
                :icon="faPlus"
                @click="onAdd"
            >
                <strong>{{ form.schema.addItemTitle || 'ADD' }}</strong>
            </Button>
        </FormInput>
    </div>
</template>

<script lang="ts">
    import { Component } from 'vue-property-decorator';

    import { faXmark, faPlus } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { JsonFormObject, JsonFormProperty } from '@evidentid/json-schema/interfaces/JsonForm';

    import { Button } from '../../Button';
    import { FormInput } from '../../Form';
    import NewButton from '../../new-button/NewButton.vue';
    import AbstractJsonSchemaForm from '../elements/AbstractJsonSchemaForm';

    @Component({
        emits: [ 'change', 'delete', 'input' ],
        inheritAttrs: false,
        components: {
            Button,
            FontAwesomeIcon,
            FormInput,
            NewButton,
        },
    })
    export default class JsonSchemaAddressForm extends AbstractJsonSchemaForm<JsonFormObject, object> {
        private faXmark = faXmark;
        private faPlus = faPlus;
        private childTouchState: Record<string, boolean> = {};
        private valueInitialized: boolean = !this.isEmptyAddress();

        public mounted() {
            // Default country to US when editing an address that has no country set.
            // Apply the default on the final getValue result to avoid the optional-field
            // stripping issue (ignoreOptionalEmpty skips optional empty enum fields).
            const val = this.value as any;
            if (this.valueInitialized && val && !val.country) {
                const emitVal = this.form.getValue({ ...val, country: 'US' }, true) as any;
                if (!emitVal.country) {
                    emitVal.country = 'US';
                }
                this.$emit('input', emitVal);
            }
        }

        private get properties() {
            return this.form.getProperties(this.value);
        }

        private updateTouchState(propName: string) {
            // Only persist touch state after the parent has triggered save-level validation.
            // Before that, suppress blur-triggered errors to follow the save-first pattern.
            if (this.touched) {
                this.childTouchState[propName] = true;
            }
        }

        private getPropValue(property: JsonFormProperty): unknown {
            return property.form.getValue((this.value as any)?.[property.name] ?? undefined);
        }

        private onInput(property: JsonFormProperty, value: any) {
            this.$emit('input', this.form.getValue({
                ...this.value,
                [property.name]: value,
            }, true));
        }

        private onChange(property: JsonFormProperty, value: any) {
            this.$emit('change', this.form.getValue({
                ...this.value,
                [property.name]: value,
            }, true));
        }

        private onAdd() {
            const newVal = this.form.getValue(undefined, true);
            // Build the final emitted value first, then apply the country default.
            // Applying it before the second getValue() call is unreliable: the
            // object getValue factory skips optional empty fields (ignoreOptionalEmpty),
            // so country can be stripped out again even after being set to 'US'.
            const emitVal = this.form.getValue(newVal, true) as any;
            if (!emitVal.country) {
                emitVal.country = 'US';
            }
            this.$emit('input', emitVal);
            this.valueInitialized = true;
        }

        private onDelete() {
            this.$emit('input', null);
            this.$emit('delete');
            this.resetFormStates();
        }

        private resetFormStates(): void {
            this.childTouchState = {};
            this.valueInitialized = false;
        }

        private isEmptyAddress(): boolean {
            return typeof this.value !== 'object' || Object.values(this.value).every((val) => val == null || val === '');
        }
    }
</script>
